import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useAuthHeaders } from "@/hooks/use-auth-headers";
import { config } from "@/config";
import { apiFetch } from "@/lib/api-client";
import { toast } from "sonner";

// ── Types ──────────────────────────────────────────────────────────────
// Non-Relational (NoSQL) ontology: DataLake → NoSQLCollection → NoSQLField
// (self-nesting via HAS_SUB_FIELD) and NoSQLCollection → NoSQLIndex. The model
// is engine-agnostic; anything engine-specific rides in the free-form
// `attributes` JSON string (see the AttributesEditor in the dialogs).

export type NoSqlOntologyLabel = "NoSQLCollection" | "NoSQLField" | "NoSQLIndex";

export interface NoSqlOntologyCollection {
  id?: string;
  name: string;
  description?: string | null;
  source?: string | null;
  fieldCount?: number;
  attributes?: string | null;
  dbOntologyId?: string;
  projectUuid?: string;
}

export interface NoSqlOntologyField {
  id?: string;
  name: string;
  fullPath: string;
  parentPath?: string | null;
  collectionId?: string;
  dataType: string;
  nullable?: boolean;
  description?: string | null;
  attributes?: string | null;
}

export interface NoSqlOntologyIndex {
  id?: string;
  name: string;
  collectionId?: string;
  fields?: string[];
  unique?: boolean;
  description?: string | null;
  attributes?: string | null;
}

// ── Shared response normalization ──────────────────────────────────────
// The backend returns either a graph-node shape ({ id, label, properties })
// or a flat object, sometimes wrapped in { success, data, total }. These
// helpers flatten both shapes so consumers can rely on a single layout.

export const unwrap = (payload: any): any => {
  if (!payload) return null;
  if (typeof payload === "object" && payload.data !== undefined && payload.success !== undefined) {
    return payload.data ?? payload;
  }
  return payload;
};

export const flattenNode = (raw: any): any => {
  if (!raw || typeof raw !== "object") return raw;
  const props =
    raw.properties && typeof raw.properties === "object" ? raw.properties : null;
  return props ? { ...raw, ...props } : raw;
};

export const normalizeListResponse = <T,>(
  payload: any,
  mapItem: (raw: any) => T,
): T[] => {
  const raw = unwrap(payload);
  if (!raw) return [];
  const candidates = [raw?.data, raw?.result, raw?.items, raw?.nodes, raw];
  for (const candidate of candidates) {
    if (Array.isArray(candidate)) return candidate.map(mapItem);
  }
  return [];
};

const normalizeCollection = (raw: any): NoSqlOntologyCollection => {
  const src = flattenNode(raw);
  return {
    ...(src?.id ? { id: String(src.id) } : {}),
    name: String(src?.name ?? ""),
    description: src?.description ?? null,
    source: src?.source ?? null,
    fieldCount: typeof src?.fieldCount === "number" ? src.fieldCount : undefined,
    attributes: src?.attributes ?? null,
    dbOntologyId: src?.dbOntologyId ?? undefined,
    projectUuid: src?.projectUuid ?? undefined,
  };
};

const normalizeField = (raw: any): NoSqlOntologyField => {
  const src = flattenNode(raw);
  return {
    ...(src?.id ? { id: String(src.id) } : {}),
    name: String(src?.name ?? ""),
    fullPath: String(src?.fullPath ?? ""),
    parentPath: src?.parentPath ?? null,
    collectionId: src?.collectionId,
    dataType: String(src?.dataType ?? ""),
    nullable: src?.nullable !== false,
    description: src?.description ?? null,
    attributes: src?.attributes ?? null,
  };
};

const normalizeIndex = (raw: any): NoSqlOntologyIndex => {
  const src = flattenNode(raw);
  return {
    ...(src?.id ? { id: String(src.id) } : {}),
    name: String(src?.name ?? ""),
    collectionId: src?.collectionId,
    fields: Array.isArray(src?.fields) ? src.fields : [],
    unique: !!src?.unique,
    description: src?.description ?? null,
    attributes: src?.attributes ?? null,
  };
};

export const parseJson = async (response: Response): Promise<any> => {
  const text = await response.text();
  if (!text) return null;
  try {
    return JSON.parse(text);
  } catch {
    return text;
  }
};

export const throwOnError = async (
  response: Response,
  fallback: string,
): Promise<void> => {
  if (response.ok) return;
  let errorMessage = `${fallback}: ${response.statusText}`;
  try {
    const errJson = await response.json();
    if (errJson?.message) errorMessage = errJson.message;
  } catch {
    /* swallow */
  }
  throw new Error(errorMessage);
};

// All write routes are ADMIN/OWNER-guarded and the role middleware reads the
// owning project from `?projectUuid` (same convention as the DDL routes).
export const withProjectUuid = (url: string, projectUuid: string) =>
  `${url}${url.includes("?") ? "&" : "?"}projectUuid=${encodeURIComponent(projectUuid)}`;

// ── Queries ────────────────────────────────────────────────────────────

interface ListOptions {
  // Required by the API: the caller must be a member of this project and the
  // data lake must belong to it. The query stays disabled until it is set.
  projectUuid?: string;
  filters?: Record<string, string | number | boolean>;
  limit?: number;
  page?: number;
}

function buildListUrl(
  dataLakeId: string,
  label: NoSqlOntologyLabel,
  options: ListOptions = {},
) {
  const params = new URLSearchParams();
  for (const [field, value] of Object.entries(options.filters ?? {})) {
    params.set(`filters[${field}][$eq]`, String(value));
  }
  if (options.projectUuid) params.set("projectUuid", options.projectUuid);
  if (options.limit !== undefined) params.set("limit", String(options.limit));
  if (options.page !== undefined) params.set("page", String(options.page));
  const qs = params.toString();
  return `${config.isometricApiUrl}/db-ontology/${encodeURIComponent(
    dataLakeId,
  )}/${label}${qs ? `?${qs}` : ""}`;
}

export function useGetNoSqlCollections(
  dataLakeId: string | undefined,
  enabled: boolean = true,
  options: ListOptions = {},
) {
  const { getAuthHeaders } = useAuthHeaders();
  return useQuery({
    queryKey: ["db-ontology", "NoSQLCollection", dataLakeId, options.limit ?? null, options.page ?? null],
    queryFn: async (): Promise<NoSqlOntologyCollection[]> => {
      const response = await apiFetch(buildListUrl(dataLakeId!, "NoSQLCollection", options), {
        method: "GET",
        headers: getAuthHeaders(),
      });
      await throwOnError(response, "Failed to fetch collections");
      return normalizeListResponse(await parseJson(response), normalizeCollection);
    },
    enabled: enabled && !!dataLakeId && !!options.projectUuid,
    staleTime: 60000,
    retry: 1,
  });
}

export interface GetNoSqlFieldsOptions extends ListOptions {
  collectionId?: string;
}

export function useGetNoSqlFields(
  dataLakeId: string | undefined,
  enabled: boolean = true,
  options: GetNoSqlFieldsOptions = {},
) {
  const { getAuthHeaders } = useAuthHeaders();
  const { collectionId, ...listOptions } = options;
  const mergedFilters = { ...(listOptions.filters ?? {}), ...(collectionId ? { collectionId } : {}) };
  return useQuery({
    queryKey: ["db-ontology", "NoSQLField", dataLakeId, collectionId ?? null, listOptions.limit ?? null, listOptions.page ?? null],
    queryFn: async (): Promise<NoSqlOntologyField[]> => {
      const response = await apiFetch(
        buildListUrl(dataLakeId!, "NoSQLField", { ...listOptions, filters: mergedFilters }),
        { method: "GET", headers: getAuthHeaders() },
      );
      await throwOnError(response, "Failed to fetch fields");
      return normalizeListResponse(await parseJson(response), normalizeField);
    },
    enabled: enabled && !!dataLakeId && !!options.projectUuid,
    staleTime: 60000,
    retry: 1,
  });
}

export interface GetNoSqlIndexesOptions extends ListOptions {
  collectionId?: string;
}

export function useGetNoSqlIndexes(
  dataLakeId: string | undefined,
  enabled: boolean = true,
  options: GetNoSqlIndexesOptions = {},
) {
  const { getAuthHeaders } = useAuthHeaders();
  const { collectionId, ...listOptions } = options;
  const mergedFilters = { ...(listOptions.filters ?? {}), ...(collectionId ? { collectionId } : {}) };
  return useQuery({
    queryKey: ["db-ontology", "NoSQLIndex", dataLakeId, collectionId ?? null, listOptions.limit ?? null, listOptions.page ?? null],
    queryFn: async (): Promise<NoSqlOntologyIndex[]> => {
      const response = await apiFetch(
        buildListUrl(dataLakeId!, "NoSQLIndex", { ...listOptions, filters: mergedFilters }),
        { method: "GET", headers: getAuthHeaders() },
      );
      await throwOnError(response, "Failed to fetch indexes");
      return normalizeListResponse(await parseJson(response), normalizeIndex);
    },
    enabled: enabled && !!dataLakeId && !!options.projectUuid,
    staleTime: 60000,
    retry: 1,
  });
}

// ── Mutations: NoSQLCollection ─────────────────────────────────────────

export interface CreateNoSqlCollectionPayload {
  projectUuid: string;
  dataLakeId: string;
  name: string;
  description?: string;
  source?: string;
  attributes?: string;
}

export function useCreateNoSqlCollection() {
  const { getAuthHeaders } = useAuthHeaders();
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (payload: CreateNoSqlCollectionPayload) => {
      const url = withProjectUuid(`${config.isometricApiUrl}/db-ontology/collection`, payload.projectUuid);
      const response = await apiFetch(url, {
        method: "POST",
        headers: getAuthHeaders(),
        body: JSON.stringify(payload),
      });
      await throwOnError(response, "Failed to create collection");
      return parseJson(response);
    },
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ queryKey: ["db-ontology", "NoSQLCollection", variables.dataLakeId] });
      toast.success("Collection created successfully");
    },
    onError: (error: Error) => {
      toast.error(error.message || "Failed to create collection");
    },
  });
}

export interface UpdateNoSqlCollectionPayload {
  id: string;
  projectUuid: string;
  dataLakeId?: string;
  data: Partial<{ name: string; description: string; source: string; attributes: string }>;
}

export function useUpdateNoSqlCollection() {
  const { getAuthHeaders } = useAuthHeaders();
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (payload: UpdateNoSqlCollectionPayload) => {
      const url = withProjectUuid(
        `${config.isometricApiUrl}/db-ontology/collection/${encodeURIComponent(payload.id)}`,
        payload.projectUuid,
      );
      const response = await apiFetch(url, {
        method: "PUT",
        headers: getAuthHeaders(),
        body: JSON.stringify(payload.data ?? {}),
      });
      await throwOnError(response, "Failed to update collection");
      return parseJson(response);
    },
    onSuccess: (_, variables) => {
      if (variables.dataLakeId) {
        queryClient.invalidateQueries({ queryKey: ["db-ontology", "NoSQLCollection", variables.dataLakeId] });
      }
      toast.success("Collection updated");
    },
    onError: (error: Error) => {
      toast.error(error.message || "Failed to update collection");
    },
  });
}

export interface DeleteNoSqlCollectionPayload {
  id: string;
  projectUuid: string;
  dataLakeId?: string;
}

export function useDeleteNoSqlCollection() {
  const { getAuthHeaders } = useAuthHeaders();
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (payload: DeleteNoSqlCollectionPayload) => {
      const url = withProjectUuid(
        `${config.isometricApiUrl}/db-ontology/collection/${encodeURIComponent(payload.id)}`,
        payload.projectUuid,
      );
      const response = await apiFetch(url, { method: "DELETE", headers: getAuthHeaders() });
      await throwOnError(response, "Failed to delete collection");
      return parseJson(response);
    },
    onSuccess: (_, variables) => {
      if (variables.dataLakeId) {
        // Deleting a collection cascades to fields and indexes.
        for (const label of ["NoSQLCollection", "NoSQLField", "NoSQLIndex"] as const) {
          queryClient.invalidateQueries({ queryKey: ["db-ontology", label, variables.dataLakeId] });
        }
      }
      toast.success("Collection deleted");
    },
    onError: (error: Error) => {
      toast.error(error.message || "Failed to delete collection");
    },
  });
}

// ── Mutations: NoSQLField ──────────────────────────────────────────────

export interface CreateNoSqlFieldPayload {
  collectionId: string;
  projectUuid: string;
  dataLakeId?: string; // for invalidation
  data: {
    name: string;
    fullPath: string;
    parentPath?: string;
    dataType: string;
    nullable?: boolean;
    description?: string;
    attributes?: string;
  };
}

export function useCreateNoSqlField() {
  const { getAuthHeaders } = useAuthHeaders();
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (payload: CreateNoSqlFieldPayload) => {
      const url = withProjectUuid(
        `${config.isometricApiUrl}/db-ontology/collection/${encodeURIComponent(payload.collectionId)}/field`,
        payload.projectUuid,
      );
      const response = await apiFetch(url, {
        method: "POST",
        headers: getAuthHeaders(),
        body: JSON.stringify(payload.data),
      });
      await throwOnError(response, "Failed to create field");
      return parseJson(response);
    },
    onSuccess: (_, variables) => {
      if (variables.dataLakeId) {
        queryClient.invalidateQueries({ queryKey: ["db-ontology", "NoSQLField", variables.dataLakeId] });
        // fieldCount on the parent collection changes — refetch the list too.
        queryClient.invalidateQueries({ queryKey: ["db-ontology", "NoSQLCollection", variables.dataLakeId] });
      }
      toast.success("Field added");
    },
    onError: (error: Error) => {
      toast.error(error.message || "Failed to create field");
    },
  });
}

export interface UpdateNoSqlFieldPayload {
  id: string;
  projectUuid: string;
  dataLakeId?: string;
  data: Partial<{ dataType: string; nullable: boolean; description: string; attributes: string }>;
}

export function useUpdateNoSqlField() {
  const { getAuthHeaders } = useAuthHeaders();
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (payload: UpdateNoSqlFieldPayload) => {
      const url = withProjectUuid(
        `${config.isometricApiUrl}/db-ontology/field/${encodeURIComponent(payload.id)}`,
        payload.projectUuid,
      );
      const response = await apiFetch(url, {
        method: "PUT",
        headers: getAuthHeaders(),
        body: JSON.stringify(payload.data ?? {}),
      });
      await throwOnError(response, "Failed to update field");
      return parseJson(response);
    },
    onSuccess: (_, variables) => {
      if (variables.dataLakeId) {
        queryClient.invalidateQueries({ queryKey: ["db-ontology", "NoSQLField", variables.dataLakeId] });
      }
      toast.success("Field updated");
    },
    onError: (error: Error) => {
      toast.error(error.message || "Failed to update field");
    },
  });
}

export interface DeleteNoSqlFieldPayload {
  id: string;
  projectUuid: string;
  dataLakeId?: string;
}

export function useDeleteNoSqlField() {
  const { getAuthHeaders } = useAuthHeaders();
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (payload: DeleteNoSqlFieldPayload) => {
      const url = withProjectUuid(
        `${config.isometricApiUrl}/db-ontology/field/${encodeURIComponent(payload.id)}`,
        payload.projectUuid,
      );
      const response = await apiFetch(url, { method: "DELETE", headers: getAuthHeaders() });
      await throwOnError(response, "Failed to delete field");
      return parseJson(response);
    },
    onSuccess: (_, variables) => {
      if (variables.dataLakeId) {
        queryClient.invalidateQueries({ queryKey: ["db-ontology", "NoSQLField", variables.dataLakeId] });
        queryClient.invalidateQueries({ queryKey: ["db-ontology", "NoSQLCollection", variables.dataLakeId] });
      }
      toast.success("Field deleted");
    },
    onError: (error: Error) => {
      toast.error(error.message || "Failed to delete field");
    },
  });
}

// ── Mutations: NoSQLIndex ──────────────────────────────────────────────

export interface CreateNoSqlIndexPayload {
  collectionId: string;
  projectUuid: string;
  dataLakeId?: string;
  data: {
    name: string;
    fields: string[];
    unique?: boolean;
    description?: string;
    attributes?: string;
  };
}

export function useCreateNoSqlIndex() {
  const { getAuthHeaders } = useAuthHeaders();
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (payload: CreateNoSqlIndexPayload) => {
      const url = withProjectUuid(
        `${config.isometricApiUrl}/db-ontology/collection/${encodeURIComponent(payload.collectionId)}/nosql-index`,
        payload.projectUuid,
      );
      const response = await apiFetch(url, {
        method: "POST",
        headers: getAuthHeaders(),
        body: JSON.stringify(payload.data),
      });
      await throwOnError(response, "Failed to create index");
      return parseJson(response);
    },
    onSuccess: (_, variables) => {
      if (variables.dataLakeId) {
        queryClient.invalidateQueries({ queryKey: ["db-ontology", "NoSQLIndex", variables.dataLakeId] });
      }
      toast.success("Index added");
    },
    onError: (error: Error) => {
      toast.error(error.message || "Failed to create index");
    },
  });
}

export interface UpdateNoSqlIndexPayload {
  id: string;
  projectUuid: string;
  dataLakeId?: string;
  data: Partial<{ fields: string[]; unique: boolean; description: string; attributes: string }>;
}

export function useUpdateNoSqlIndex() {
  const { getAuthHeaders } = useAuthHeaders();
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (payload: UpdateNoSqlIndexPayload) => {
      const url = withProjectUuid(
        `${config.isometricApiUrl}/db-ontology/nosql-index/${encodeURIComponent(payload.id)}`,
        payload.projectUuid,
      );
      const response = await apiFetch(url, {
        method: "PUT",
        headers: getAuthHeaders(),
        body: JSON.stringify(payload.data ?? {}),
      });
      await throwOnError(response, "Failed to update index");
      return parseJson(response);
    },
    onSuccess: (_, variables) => {
      if (variables.dataLakeId) {
        queryClient.invalidateQueries({ queryKey: ["db-ontology", "NoSQLIndex", variables.dataLakeId] });
      }
      toast.success("Index updated");
    },
    onError: (error: Error) => {
      toast.error(error.message || "Failed to update index");
    },
  });
}

export interface DeleteNoSqlIndexPayload {
  id: string;
  projectUuid: string;
  dataLakeId?: string;
}

export function useDeleteNoSqlIndex() {
  const { getAuthHeaders } = useAuthHeaders();
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (payload: DeleteNoSqlIndexPayload) => {
      const url = withProjectUuid(
        `${config.isometricApiUrl}/db-ontology/nosql-index/${encodeURIComponent(payload.id)}`,
        payload.projectUuid,
      );
      const response = await apiFetch(url, { method: "DELETE", headers: getAuthHeaders() });
      await throwOnError(response, "Failed to delete index");
      return parseJson(response);
    },
    onSuccess: (_, variables) => {
      if (variables.dataLakeId) {
        queryClient.invalidateQueries({ queryKey: ["db-ontology", "NoSQLIndex", variables.dataLakeId] });
      }
      toast.success("Index deleted");
    },
    onError: (error: Error) => {
      toast.error(error.message || "Failed to delete index");
    },
  });
}
